import { useState } from "react";
import {
  Box,
  Button,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { mdiClose, mdiPlus } from "@mdi/js";
import Icon from "@mdi/react";

import { MoneyManagerCategory, MoneyManagerData, MoneyManagerSubcategory } from "api/money-manager";
import { noSpinnerSx } from "utils/sx";

function uid(): string {
  return Math.random().toString(36).slice(2, 9);
}

function sumSubcategories(subcategories: MoneyManagerSubcategory[]): number {
  return subcategories.reduce((sum, sc) => sum + (parseFloat(sc.cost) || 0), 0);
}

export interface SetupProps {
  data: MoneyManagerData;
  isAdmin: boolean;
  isSaving: boolean;
  onSave: (data: MoneyManagerData) => void;
}

export default function Setup(props: SetupProps) {
  const { data, isAdmin, isSaving, onSave } = props;
  // local copy so edits are batched before saving
  const [draft, setDraft] = useState<MoneyManagerData>(data);

  function save(updated: MoneyManagerData) {
    setDraft(updated);
    onSave(updated);
  }

  function handleCurrencyChange(currency: string) {
    save({ ...draft, currency });
  }

  function addCategory() {
    const newCat: MoneyManagerCategory = {
      id: uid(),
      name: "",
      type: "per_unit",
      total: "",
      subcategories: [],
    };
    save({ ...draft, categories: [...draft.categories, newCat] });
  }

  function removeCategory(catId: string) {
    save({ ...draft, categories: draft.categories.filter((c) => c.id !== catId) });
  }

  function updateCategory(catId: string, field: keyof MoneyManagerCategory, value: string) {
    save({
      ...draft,
      categories: draft.categories.map((c) => (c.id === catId ? { ...c, [field]: value } : c)),
    });
  }

  function addSubcategory(catId: string) {
    const newSc: MoneyManagerSubcategory = { id: uid(), name: "", cost: "" };
    const categories = draft.categories.map((c) => {
      if (c.id !== catId) return c;
      const subcategories = [...c.subcategories, newSc];
      return { ...c, subcategories, total: sumSubcategories(subcategories).toString() };
    });
    save({ ...draft, categories });
  }

  function removeSubcategory(catId: string, scId: string) {
    const categories = draft.categories.map((c) => {
      if (c.id !== catId) return c;
      const subcategories = c.subcategories.filter((sc) => sc.id !== scId);
      return { ...c, subcategories, total: sumSubcategories(subcategories).toString() };
    });
    save({ ...draft, categories });
  }

  function updateSubcategory(catId: string, scId: string, field: keyof MoneyManagerSubcategory, value: string) {
    const categories = draft.categories.map((c) => {
      if (c.id !== catId) return c;
      const subcategories = c.subcategories.map((sc) => (sc.id === scId ? { ...sc, [field]: value } : sc));
      return { ...c, subcategories, total: sumSubcategories(subcategories).toString() };
    });
    save({ ...draft, categories });
  }

  const readonly = !isAdmin;

  return (
    <Stack spacing={3} maxWidth='sm' margin='auto' pt={2}>
      {/* Currency */}
      <Box>
        <FormControl size='small' disabled={readonly} fullWidth>
          <InputLabel>Currency</InputLabel>
          <Select label='Currency' value={draft.currency} onChange={(e) => handleCurrencyChange(e.target.value)}>
            {["RON", "EUR", "USD", "GBP", "CHF", "HUF"].map((cur) => (
              <MenuItem key={cur} value={cur}>
                {cur}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Divider />

      {/* Categories */}
      <Box>
        <Box display='flex' justifyContent='space-between' alignItems='center' mb={2}>
          <Typography variant='subtitle2' color='text.secondary' sx={{ textTransform: "uppercase", letterSpacing: 1 }}>
            Cost Categories
          </Typography>
          {isAdmin && (
            <Button
              size='small'
              startIcon={<Icon path={mdiPlus} size={0.7} />}
              onClick={addCategory}
              disabled={isSaving}
            >
              Add
            </Button>
          )}
        </Box>

        {draft.categories.length === 0 && (
          <Typography variant='body2' color='text.secondary' textAlign='center' py={2}>
            No categories yet. {isAdmin ? "Add one above." : ""}
          </Typography>
        )}

        <Stack spacing={2}>
          {draft.categories.map((cat) => {
            const hasSubs = cat.subcategories.length > 0;
            const computedTotal = hasSubs ? sumSubcategories(cat.subcategories) : null;

            return (
              <Paper key={cat.id} variant='outlined' sx={{ p: 2, position: "relative" }}>
                {isAdmin && (
                  <IconButton
                    size='small'
                    sx={{ position: "absolute", top: 4, right: 4 }}
                    onClick={() => removeCategory(cat.id)}
                    disabled={isSaving}
                    aria-label='Delete category'
                  >
                    <Icon path={mdiClose} size={0.7} />
                  </IconButton>
                )}
                <Stack spacing={2}>
                  {/* Row: name / type / total / delete */}
                  <Box display='flex' gap={1.5} alignItems='center' flexWrap='wrap' pr={isAdmin ? 4 : 0}>
                    <TextField
                      label='Name'
                      placeholder='e.g. Meals'
                      size='small'
                      value={cat.name}
                      disabled={readonly}
                      onChange={(e) => updateCategory(cat.id, "name", e.target.value)}
                      sx={{ flex: 1, minWidth: 120 }}
                    />
                    <FormControl size='small' disabled={readonly} sx={{ minWidth: 90 }}>
                      <InputLabel>Type</InputLabel>
                      <Select
                        label='Type'
                        value={cat.type}
                        onChange={(e) => updateCategory(cat.id, "type", e.target.value)}
                      >
                        <MenuItem value='per_unit'>Per unit</MenuItem>
                        <MenuItem value='addon'>Add-on</MenuItem>
                      </Select>
                    </FormControl>

                    {!hasSubs && (
                      <TextField
                        label='Total '
                        size='small'
                        type='number'
                        inputProps={{ min: 0 }}
                        value={hasSubs ? (computedTotal ?? 0).toFixed(2) : cat.total}
                        disabled={readonly || hasSubs}
                        onChange={(e) => updateCategory(cat.id, "total", e.target.value)}
                        sx={{ width: 80, ...noSpinnerSx }}
                      />
                    )}
                  </Box>

                  {/* Subcategories */}
                  {cat.subcategories.map((sc) => (
                    <Box key={sc.id} display='flex' gap={1.5} alignItems='center' pl={2} flexWrap='wrap'>
                      <TextField
                        label='Subcategory'
                        placeholder='e.g. Kaufland'
                        size='small'
                        value={sc.name}
                        disabled={readonly}
                        onChange={(e) => updateSubcategory(cat.id, sc.id, "name", e.target.value)}
                        sx={{ flex: 1, minWidth: 120 }}
                      />
                      <TextField
                        label='Cost'
                        size='small'
                        type='number'
                        inputProps={{ min: 0 }}
                        value={sc.cost}
                        disabled={readonly}
                        onChange={(e) => updateSubcategory(cat.id, sc.id, "cost", e.target.value)}
                        sx={{ width: 80, ...noSpinnerSx }}
                      />
                      {isAdmin && (
                        <IconButton
                          sx={{ flexShrink: 0 }}
                          size='small'
                          onClick={() => removeSubcategory(cat.id, sc.id)}
                          disabled={isSaving}
                        >
                          <Icon path={mdiClose} size={0.7} />
                        </IconButton>
                      )}
                    </Box>
                  ))}

                  {hasSubs && (
                    <Typography display='flex' justifyContent='end' variant='caption' color='textDisabled' pl={2}>
                      Subcategory total: {computedTotal?.toFixed(2)} {draft.currency}
                    </Typography>
                  )}

                  {isAdmin && (
                    <Button
                      size='small'
                      variant='text'
                      sx={{ alignSelf: "flex-start", ml: 2 }}
                      onClick={() => addSubcategory(cat.id)}
                      disabled={isSaving}
                    >
                      + Subcategory
                    </Button>
                  )}
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      </Box>
    </Stack>
  );
}
